/* ==========================================================================
   ProTrade Commerce — animations.js
   Intersection Observer reveals, button ripple, hero parallax, counters.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.theme;
  const { $, $$, throttle } = T.utils;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -------------------------------------------------------- reveal on scroll */

  let observer = null;

  function initReveal(scope) {
    if (!T.settings.animations || reduceMotion.matches) {
      $$('[data-animate]', scope || document).forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!('IntersectionObserver' in window)) {
      $$('[data-animate]', scope || document).forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    $$('[data-animate]', scope || document).forEach((el) => {
      if (el._observed) return;
      el._observed = true;

      // Anything already in view on load reveals immediately, no flash.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
        return;
      }
      observer.observe(el);
    });
  }

  /* ----------------------------------------------------------- stagger */

  function initStagger(scope) {
    $$('[data-animate-stagger]', scope || document).forEach((group) => {
      const step = parseInt(group.dataset.staggerStep || '70', 10);
      Array.from(group.children).forEach((child, index) => {
        child.style.setProperty('--anim-delay', `${Math.min(index, 9) * step}ms`);
      });
    });
  }

  /* ------------------------------------------------------------ ripple */

  function initRipple() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.btn--primary, .btn--secondary, [data-ripple]');
      if (!button || button.dataset.ripple === 'false') return;
      if (reduceMotion.matches) return;

      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const ripple = document.createElement('span');
      ripple.className = 'btn__ripple';
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`;

      button.appendChild(ripple);
      setTimeout(() => ripple.remove(), 640);
    });
  }

  /* ---------------------------------------------------------- parallax */

  function initParallax() {
    const targets = $$('[data-parallax]');
    if (!targets.length || reduceMotion.matches) return;

    const onScroll = throttle(() => {
      const viewportHeight = window.innerHeight;
      targets.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewportHeight) return;
        const speed = parseFloat(el.dataset.parallax) || 0.12;
        const centerOffset = rect.top + rect.height / 2 - viewportHeight / 2;
        el.style.transform = `translate3d(0, ${(-centerOffset * speed).toFixed(2)}px, 0)`;
      });
    }, 16);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ------------------------------------------------ pointer-tracked glow */

  function initPointerGlow() {
    if (reduceMotion.matches) return;
    $$('[data-pointer-glow]').forEach((el) => {
      el.addEventListener('pointermove', (event) => {
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--pointer-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        el.style.setProperty('--pointer-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      });
    });
  }

  /* --------------------------------------------------------- smooth anchors */

  function initSmoothAnchors() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]:not([href="#"])');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  /* --------------------------------------------------------------- init */

  function init() {
    initReveal();
    initStagger();
    initRipple();
    initParallax();
    initPointerGlow();
    initSmoothAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('theme:section:load', (event) => {
    initReveal(event.detail.target);
    initStagger(event.detail.target);
  });

  T.initReveal = initReveal;
})();
